import { useEffect, useState } from 'react'
import InlineField from './InlineField'

interface Props {
  /** 0/1 aus der API (SQLite kennt kein Boolean). */
  checked: number | boolean | null | undefined
  employeeName: string | null | undefined
  /** Speichert ein Feld — dieselbe Signatur wie bei InlineField. */
  onSave: (field: string, value: string | number | null) => Promise<unknown> | unknown
  labelWidth?: number
  disabled?: boolean
}

/**
 * „Mitarbeiter-Rechnung" — Haken plus Namensfeld (Bugtracker #124).
 *
 * Belege werden regelmaessig von Monteuren aus eigener Tasche bezahlt und spaeter
 * erstattet. Der Haken macht sie in der Buchhaltung als „MT" sichtbar, das
 * Namensfeld sagt, wer das Geld zurueckbekommt.
 *
 * Ein Baustein fuer Buchhaltung UND Dokumentenablage — die beiden Detail-Modals
 * sind bewusst baugleich (Kundenwunsch „einheitlicher Look"), und der Vorgaenger
 * dieser Funktion ist bei der shadcn-Migration genau daran gescheitert, dass die
 * Buchhaltung eine eigene Kopie hatte und die Ablage eine zweite.
 *
 * Das Namensfeld erscheint erst mit gesetztem Haken — ohne Haken hat es keine
 * Bedeutung, und eine leere Zeile in einer Faktenliste liest sich wie ein
 * fehlender Wert.
 */
export default function EmployeeInvoiceField({
  checked, employeeName, onSave, labelWidth = 104, disabled,
}: Props) {
  const isOn = !!checked
  // Optimistisch, damit das Namensfeld sofort erscheint und nicht erst nach dem
  // Round-Trip — der Haken ist der Weg zum eigentlichen Eingabefeld.
  const [local, setLocal] = useState(isOn)
  useEffect(() => { setLocal(isOn) }, [isOn])

  const toggle = (next: boolean) => {
    setLocal(next)
    // Haken raus → auch den Namen loeschen. Ein stehengebliebener Name ohne
    // Markierung taucht in keiner Ansicht mehr auf und wird beim naechsten
    // Setzen des Hakens zur stillen Falschaussage.
    void Promise.resolve(onSave('is_employee_invoice', next ? 1 : 0))
      .then(() => (!next && employeeName ? onSave('employee_name', null) : undefined))
      .catch(() => setLocal(isOn))
  }

  return (
    <>
      <div
        className="grid min-h-[30px] items-center gap-2 border-t border-gray-100 px-3 py-1 first:border-t-0"
        style={{ gridTemplateColumns: `${labelWidth > 0 ? `${labelWidth}px ` : ''}minmax(0,1fr)` }}
      >
        {labelWidth > 0 && <span className="text-[11px] text-gray-400">Bezahlt von</span>}
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={local}
            disabled={disabled}
            onChange={(e) => toggle(e.target.checked)}
            className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
          />
          <span className="text-gray-700">Mitarbeiter</span>
        </label>
      </div>

      {local && (
        <InlineField
          label="Name"
          field="employee_name"
          value={employeeName}
          placeholder="Wer hat ausgelegt?"
          labelWidth={labelWidth}
          readOnly={disabled}
          onSave={(field, value) => Promise.resolve(onSave(field, value))}
        />
      )}
    </>
  )
}
